import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { routes } from '@app/consts/routes';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ArticleVerbose } from '../../models/article.model';
import { ArticlesService } from '../../services/articles.service';
import { FeatureService } from '@app/core/services/feature.service';
import { features } from '@app/consts/features';
import { SecurityProtectedBase } from '@app/shared/components/security-protected/security-protected';
import { title } from 'process';

@Component({
  selector: 'app-articles-editor',
  templateUrl: './articles-editor.component.html',
  styleUrls: ['./articles-editor.component.scss']
})
export class ArticlesEditorComponent extends SecurityProtectedBase implements OnInit {
  formId: string = 'frm_egKPtUgOUtJSWc';
  changeReasonFormId: string = 'frm_UBkYJHhQx0eQz2';
  loading: boolean = false;
  formData: any;
  formPristine: any;
  formValid: any;
  article: ArticleVerbose;
  fileToUpload: File | null = null;
  articleImage$: any;
  filePreview: SafeUrl;
  hasFeatureAccess: any;

  constructor(
    private overlayService: OverlayService,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private articlesService: ArticlesService,
    private dialog: MatDialog,
    public sanitizer: DomSanitizer,
    featureService: FeatureService
  ) {
    super(featureService, features.ARTICLES);
  }

  ngOnInit(): void {
    if (this.hasFeatureAccess) {
      const id = this.getIdInURL();
  
      if (id) {
        this.getArticle(id);
      }
      else {
        this.buildForm();
      }
    }
  }

  buildForm() {
    this.formData = {
      title: this.article ? this.article.title : null,
      content: this.article ? this.article.content : null,
      splashText: this.article ? this.article.splashText : null,
      publishOn: this.article ? this.article.publishOn : null,
      expiresOn: this.article ? this.article.expiresOn : null,
      showImageOnSplashPage: this.article ? this.article.showImageOnSplashPage : false,
      regions: this.article ? this.article.regions.map(r => r.id) : [],
      titleImage: this.article ? (this.article.titleImage ? this.article.titleImage.id : null) : null,
    };
  }

  getIdInURL(): string {
    let IdInURL: string;

    this.route.paramMap.subscribe(
      params => IdInURL = params.get('articleId')
    );

    return IdInURL;
  }

  getArticle(id: string) {
    this.overlayService.show('Loading');
    this.loading = true;

    this.articlesService.getArticle(id).subscribe({
      next: (res) => {
        this.article = res;
        this.overlayService.hide();
        this.loading = false;
        if (this.article.files && this.article.files.length > 0) {
          this.articleImage$ = this.articlesService.getEmployeeArticleFile(this.article.id, this.article.files[0].id);
        }
        this.buildForm();
      },
      error: (e) => { this.overlayService.hide(); this.loading = false; },
      complete: () => this.overlayService.hide()
    });
  }
  
  backToArticles() {
    if (this.formPristine === false) {
      this.openConfirmCloseDialog();
    }
    else {
      this.router.navigate([`${routes.SITE_SETTINGS_NO_SLASH}${routes.ARTICLES}`]);
    }
  }

  formDataEmitted(formData) {
    this.formData = formData;
    this.fileToUpload = this.formData?.files?.length > 0 ? this.formData.files[0] : null;
    if (this.fileToUpload) {
      this.filePreview = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(this.fileToUpload));
    }
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  formPristineEmitted(formPristine: boolean) {
    this.formPristine = formPristine;
  }

  save() {
    this.overlayService.show();

    if (this.formValid) {
      if (this.article) {
        this.articlesService.editArticle(this.article.id, this.formData)
          .subscribe(
            {
              next: (v) => {
                this.overlayService.hide();
                this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
                this.formPristine = true;
                if (this.fileToUpload) {
                  this.deleteFiles();
                }
              },
              error: (e) => {
                this.overlayService.hide();
              }
            }
          );
      }
      else {
        this.formData.files = [];
        this.articlesService.createArticle(this.formData)
          .subscribe(
            {
              next: (v) => {
                this.overlayService.hide();
                this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
                if (this.fileToUpload) {
                  this.addImage(v.id);
                }
                else {
                  this.router.navigate([`${routes.SITE_SETTINGS_NO_SLASH}${routes.ARTICLES}${routes.EDITOR}/${v.id}`]);
                }
              },
              error: (e) => {
                this.overlayService.hide();
              }
            }
          );
      }
    }
  }

  addImage(articleId: string) {
    this.overlayService.show();

    this.articlesService.addArticleFile(articleId, this.fileToUpload)
    .subscribe(
          {
            next: (v) => {
              this.overlayService.hide();
              this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
              this.formPristine = true;
              this.setImageAsTitleImage(articleId, v.id);
            },
            error: (e) => {
              this.overlayService.hide();
            }
          }
        );
  }

  deleteFiles() {
    if (this.article && this.article.files && this.article.files.length > 0) {
      this.overlayService.show();

      const deleteFileObservables = this.article.files.map(file =>
        this.articlesService.deleteArticleFile(this.article.id, file.id)
      );

      Promise.all(deleteFileObservables.map(obs => obs.toPromise()))
        .then(() => {
          this.overlayService.hide();
          this.snackbarService.openSnackBar(`${this.translate.instant('FilesDeletedSuccessfully')}`, 'clear', 'success');
          this.addImage(this.article.id);
        })
        .catch(() => {
          this.overlayService.hide();
        });
    }
  }

  setImageAsTitleImage(articleId: string, imageId: string) {
    this.overlayService.show();

    this.formData.titleImage = imageId;

    this.articlesService.editArticle(articleId, this.formData)
    .subscribe(
      {
        next: (v) => {
          this.overlayService.hide();
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.formPristine = true;
          if(!this.article) {
            this.router.navigate([`${routes.SITE_SETTINGS_NO_SLASH}${routes.ARTICLES}${routes.EDITOR}/${articleId}`]);
          }
        },
        error: (e) => {
          this.overlayService.hide();
        }
      }
    );
  }

  refresh() {
    const id = this.getIdInURL();

    if (id) {
      this.getArticle(id);
    }
    else {
      this.buildForm();
    }
  }

  openConfirmCloseDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('UnsavedChangesMessage')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.router.navigate([`${routes.SITE_SETTINGS_NO_SLASH}${routes.ARTICLES}`]);
        }
      }
    );
  }
}
